import React, { useState, useEffect } from "react";

/**
 * Reusable ranked analytics table.
 *
 * Props:
 *   title       - card header title
 *   subtitle    - card header subtitle
 *   badge       - pill badge text (e.g. "Top 10")
 *   badgeColor  - Tailwind color key: "blue" | "emerald" | "violet" | "indigo" | "amber"
 *   columns     - [{ label, align?, width?, renderSkeletonCell? }]   align: "left" (default) | "right" | "center"
 *   rows        - array of row objects
 *   renderRow   - (row, index, rows) => <td> elements (no <tr> wrapper needed — component adds it)
 *   colSpan     - number of columns (for empty state colspan)
 *   emptyText   - text when rows is empty
 *   emptyContent - optional ReactNode rendered instead of emptyText when rows is empty
 *   loading     - shows pulsing skeleton rows instead of rows/empty-state while true
 *   skeletonRows - number of skeleton rows to render while loading (default 5)
 *   pageSize    - if set, paginates rows client-side and shows Prev/Next controls
 */

const BADGE_STYLES = {
  blue:    "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600",
  violet:  "bg-violet-50 text-violet-600",
  indigo:  "bg-indigo-50 text-indigo-600",
  amber:   "bg-amber-50 text-amber-600",
};

const RankedTable = ({
  title,
  subtitle,
  badge,
  badgeColor = "blue",
  columns = [],
  rows = [],
  renderRow,
  colSpan,
  emptyText = "No data available",
  emptyContent,
  loading = false,
  skeletonRows = 5,
  pageSize,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset back to page 1 whenever the underlying data changes (e.g. new filters applied)
  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  const totalPages = pageSize ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  const pagedRows = pageSize
    ? rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : rows;

  return (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    {/* Header */}
    {(title || subtitle || badge) && (
      <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-100">
        <div className="flex flex-col">
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {badge && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BADGE_STYLES[badgeColor] || BADGE_STYLES.blue}`}>
            {badge}
          </span>
        )}
      </div>
    )}

    {/* Table */}
    <table className="w-full text-xs modern-table">
      {columns.some(c => c.width) && (
        <colgroup>
          {columns.map((col, i) => <col key={i} style={col.width ? { width: col.width } : {}} />)}
        </colgroup>
      )}
      <thead>
        <tr className="border-b border-gray-100">
          {columns.map((col, i) => (
            <th
              key={i}
              className={`px-5 py-3 font-medium uppercase tracking-wide text-[10px] ${
                col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
              }`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {loading ? (
          Array.from({ length: skeletonRows }).map((_, i) => (
            <tr key={i}>
              {columns.map((col, j) => (
                <td key={j} className="px-5 py-3">
                  {col.renderSkeletonCell ? (
                    col.renderSkeletonCell()
                  ) : (
                    <div
                      className="h-3 rounded bg-gray-100 animate-pulse"
                      style={{
                        width: col.align === "right" ? "40px" : col.align === "center" ? "60px" : "80%",
                        marginLeft: col.align === "right" ? "auto" : undefined,
                      }}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={colSpan || columns.length} className="px-5 py-6 text-center text-gray-400">
              {emptyContent || emptyText}
            </td>
          </tr>
        ) : (
          pagedRows.map((row, i) => {
            const globalIndex = pageSize ? (currentPage - 1) * pageSize + i : i;
            return (
              <tr key={globalIndex} className="hover:bg-gray-50 transition-colors">
                {renderRow(row, globalIndex, pagedRows)}
              </tr>
            );
          })
        )}
      </tbody>
    </table>

    {/* Pagination footer */}
    {pageSize && !loading && rows.length > pageSize && (
      <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 text-xs text-gray-500">
        <span>
          Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} of {rows.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2.5 py-1 rounded-md border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    )}
  </div>
  );
};

export default RankedTable;
